import React, { useEffect, useState, useRef } from 'react';
import { Plus, Search, Download, Users, Building2, Edit2, Trash2, Eye, X, Upload, UserPlus, FileSpreadsheet, AlertCircle, CheckCircle2, ChevronUp, ChevronDown } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { learnersService } from '../services/learners';
import { organizationsService } from '../services/organizations';
import { structuresService } from '../services/structures';
import { trainingProgramsService } from '../services/trainingPrograms';
import { enrollmentsService } from '../services/enrollments';
import { Learner, LearnerWithProgress, LearnerStatus, Organization, Structure } from '../types';
import { validateAndSanitizeLearner, sanitizeText, sanitizeEmail } from '../utils/sanitize';
import { safeText } from '../components/SafeText';
import * as XLSX from 'xlsx';

export const Learners: React.FC = () => {
  const { isSuperAdmin, getUserOrganizationId } = usePermissions();

  const [learners, setLearners] = useState<LearnerWithProgress[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [structures, setStructures] = useState<Structure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<LearnerStatus | ''>('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLearner, setEditingLearner] = useState<LearnerWithProgress | null>(null);
  const [viewingLearner, setViewingLearner] = useState<LearnerWithProgress | null>(null);
  const [deletingLearner, setDeletingLearner] = useState<LearnerWithProgress | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Tri des colonnes
  type SortColumn = 'fullname' | 'department' | 'formation' | 'enrolledAt' | 'completedAt';
  type SortDirection = 'asc' | 'desc';
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadLearners();
  }, [selectedOrgId]);

  const loadInitialData = async () => {
    try {
      setError(null);
      if (isSuperAdmin) {
        const orgs = await organizationsService.getAll();
        setOrganizations(orgs);
        if (orgs.length > 0) {
          setSelectedOrgId(orgs[0].id);
        }
      } else {
        const orgId = getUserOrganizationId();
        if (orgId) {
          setSelectedOrgId(orgId);
        } else {
          setError('Aucune organisation associée à votre compte');
        }
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadLearners = async () => {
    if (!selectedOrgId) return;

    try {
      setError(null);
      const [learnersData, structuresData] = await Promise.all([
        learnersService.getByOrganization(selectedOrgId),
        structuresService.getByOrganization(selectedOrgId)
      ]);
      setLearners(learnersData);
      setStructures(structuresData);
    } catch (err) {
      console.error('Error loading learners:', err);
      setError('Erreur lors du chargement des apprenants');
    }
  };

  const handleDeleteClick = (learner: LearnerWithProgress) => {
    setDeletingLearner(learner);
  };

  const confirmDelete = async () => {
    if (!deletingLearner) return;

    setIsDeleting(true);
    try {
      await learnersService.delete(deletingLearner.id);
      setLearners(prev => prev.filter(l => l.id !== deletingLearner.id));
      setDeletingLearner(null);
    } catch (error) {
      console.error('Error deleting learner:', error);
      alert('Impossible de supprimer l\'apprenant');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (learner: LearnerWithProgress) => {
    setEditingLearner(learner);
    setIsModalOpen(true);
  };

  const handleView = async (learnerId: string) => {
    try {
      const learnerWithProgress = await learnersService.getWithProgress(learnerId);
      setViewingLearner(learnerWithProgress);
    } catch (error) {
      console.error('Error loading learner details:', error);
    }
  };

  const handleCreate = () => {
    console.log('handleCreate called, selectedOrgId:', selectedOrgId);
    setEditingLearner(null);
    setIsModalOpen(true);
    console.log('isModalOpen set to true');
  };

  const handleSave = async (learnerData: Partial<Learner>) => {
    try {
      if (editingLearner) {
        await learnersService.update(editingLearner.id, learnerData);
      } else {
        // Utiliser bulkCreate pour gérer le cas où l'apprenant existe déjà
        // bulkCreate retourne l'apprenant existant si l'email existe déjà
        await learnersService.bulkCreate([{
          ...learnerData,
          organizationId: selectedOrgId
        } as Omit<Learner, 'id' | 'createdAt' | 'updatedAt'>]);
      }
      setIsModalOpen(false);
      loadLearners();
    } catch (error) {
      console.error('Error saving learner:', error);
      alert('Impossible de sauvegarder l\'apprenant');
    }
  };

  const handleExport = () => {
    // Create one row per enrollment
    const rows: { 'Nom et Prénom': string; 'Groupe': string; 'Email': string; 'Formation': string; "Date d'inscription": string; 'Date de complétion': string }[] = [];

    filteredLearners.forEach(learner => {
      const enrollments = learner.enrollments || [];

      if (enrollments.length === 0) {
        // Learner with no formations
        rows.push({
          'Nom et Prénom': learner.fullname,
          'Groupe': learner.department || '',
          'Email': learner.email,
          'Formation': '',
          "Date d'inscription": '',
          'Date de complétion': ''
        });
      } else {
        // One row per formation
        enrollments.forEach(enrollment => {
          rows.push({
            'Nom et Prénom': learner.fullname,
            'Groupe': learner.department || '',
            'Email': learner.email,
            'Formation': enrollment.program?.name || '',
            "Date d'inscription": enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString('fr-FR') : '',
            'Date de complétion': enrollment.completedAt ? new Date(enrollment.completedAt).toLocaleDateString('fr-FR') : ''
          });
        });
      }
    });

    if (rows.length === 0) return;

    const csv = [
      Object.keys(rows[0]).join(','),
      ...rows.map(row => Object.values(row).map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `apprenants_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Fonction pour gérer le clic sur une colonne de tri
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Si même colonne, inverser la direction
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      // Nouvelle colonne, commencer par décroissant
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Filter and sort learners
  const filteredLearners = learners
    .filter(learner => {
      const matchesSearch = !searchQuery ||
        learner.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        learner.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        learner.department?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = !statusFilter || learner.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;

      let valueA: string | number | null = null;
      let valueB: string | number | null = null;

      switch (sortColumn) {
        case 'fullname':
          valueA = a.fullname.toLowerCase();
          valueB = b.fullname.toLowerCase();
          break;
        case 'department':
          valueA = (a.department || '').toLowerCase();
          valueB = (b.department || '').toLowerCase();
          break;
        case 'formation':
          // Trier par le nom de la première formation
          valueA = (a.enrollments?.[0]?.program?.name || '').toLowerCase();
          valueB = (b.enrollments?.[0]?.program?.name || '').toLowerCase();
          break;
        case 'enrolledAt':
          // Trier par la date d'inscription la plus récente
          const dateA = a.enrollments?.[0]?.enrolledAt;
          const dateB = b.enrollments?.[0]?.enrolledAt;
          valueA = dateA ? new Date(dateA).getTime() : 0;
          valueB = dateB ? new Date(dateB).getTime() : 0;
          break;
        case 'completedAt':
          // Trier par la date de complétion (les non-complétées à la fin)
          const compA = a.enrollments?.[0]?.completedAt;
          const compB = b.enrollments?.[0]?.completedAt;
          valueA = compA ? new Date(compA).getTime() : (sortDirection === 'desc' ? -Infinity : Infinity);
          valueB = compB ? new Date(compB).getTime() : (sortDirection === 'desc' ? -Infinity : Infinity);
          break;
      }

      if (valueA === null || valueB === null) return 0;
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isSuperAdmin ? 'Apprenants' : 'Mes apprenants'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {filteredLearners.length} apprenant{filteredLearners.length > 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            disabled={filteredLearners.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-5 h-5" />
            Exporter
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleCreate}
              disabled={!selectedOrgId}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <Plus className="w-5 h-5" />
              Ajouter
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {isSuperAdmin && (
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-400" />
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            >
              <option value="">Sélectionner une organisation</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un apprenant..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as LearnerStatus | '')}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
        >
          <option value="">Tous les statuts</option>
          <option value="active">En formation</option>
          <option value="completed">Terminé</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>

      {/* Learners Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('fullname')}
                >
                  <div className="flex items-center gap-1">
                    Nom et Prénom
                    {sortColumn === 'fullname' && (
                      sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center gap-1">
                    Groupe
                    {sortColumn === 'department' && (
                      sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('formation')}
                >
                  <div className="flex items-center gap-1">
                    Formations
                    {sortColumn === 'formation' && (
                      sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('enrolledAt')}
                >
                  <div className="flex items-center gap-1">
                    Date d'inscription
                    {sortColumn === 'enrolledAt' && (
                      sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('completedAt')}
                >
                  <div className="flex items-center gap-1">
                    Date de complétion
                    {sortColumn === 'completedAt' && (
                      sortDirection === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {!selectedOrgId ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Sélectionnez une organisation pour voir ses apprenants</p>
                  </td>
                </tr>
              ) : filteredLearners.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Aucun apprenant trouvé
                  </td>
                </tr>
              ) : (
                filteredLearners.map((learner) => (
                  <tr key={learner.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {safeText(learner.fullname)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {safeText(learner.email)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                      {safeText(learner.department) || '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                      {learner.enrollments && learner.enrollments.length > 0 ? (
                        <div className="space-y-1">
                          {learner.enrollments.map((e, idx) => (
                            <div key={idx} className="text-sm">
                              {safeText(e.program?.name) || 'Formation'}
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                      {learner.enrollments && learner.enrollments.length > 0 ? (
                        <div className="space-y-1">
                          {learner.enrollments.map((e, idx) => (
                            <div key={idx} className="text-sm">
                              {e.enrolledAt ? new Date(e.enrolledAt).toLocaleDateString('fr-FR') : '-'}
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                      {learner.enrollments && learner.enrollments.length > 0 ? (
                        <div className="space-y-1">
                          {learner.enrollments.map((e, idx) => (
                            <div key={idx} className="text-sm">
                              {e.completedAt ? (
                                <span className="text-green-600 dark:text-green-400">
                                  {new Date(e.completedAt).toLocaleDateString('fr-FR')}
                                </span>
                              ) : (
                                <span className="text-orange-500 dark:text-orange-400">En cours</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleView(learner.id)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isSuperAdmin && (
                          <>
                            <button
                              onClick={() => handleEdit(learner)}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(learner)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <LearnerModal
          key={editingLearner?.id || 'new'}
          learner={editingLearner}
          structures={structures}
          organizationId={selectedOrgId}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
        />
      )}

      {/* View Details Modal */}
      {viewingLearner && (
        <LearnerDetailsModal
          key={viewingLearner.id}
          learner={viewingLearner}
          onClose={() => setViewingLearner(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingLearner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 dark:text-white mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-2">
                Êtes-vous sûr de vouloir supprimer l'apprenant :
              </p>
              <p className="text-center font-medium text-gray-900 dark:text-white mb-1">
                {safeText(deletingLearner.fullname)}
              </p>
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                {safeText(deletingLearner.email)}
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-6">
                <p className="text-sm text-red-700 dark:text-red-300 text-center">
                  Cette action est irréversible. Toutes les inscriptions aux formations de cet apprenant seront également supprimées.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingLearner(null)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Learner Status Badge
const LearnerStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
  };

  const labels = {
    active: 'En formation',
    completed: 'Terminé',
    inactive: 'Inactif'
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles] || styles.inactive}`}>
      {labels[status as keyof typeof labels] || status}
    </span>
  );
};

// Learner Modal Component
type ModalMode = 'choice' | 'manual' | 'import';

interface LearnerModalProps {
  learner: LearnerWithProgress | null;
  structures: Structure[];
  organizationId: string;
  onClose: () => void;
  onSave: (data: Partial<Learner>) => void;
}

interface ImportedLearner {
  fullname: string;
  email: string;
  groupe?: string;
  formation?: string;
  dateInscription?: string;
  dateCompletion?: string;
  validationErrors?: string[];
  isValid?: boolean;
}

const LearnerModal: React.FC<LearnerModalProps> = ({
  learner,
  structures,
  organizationId,
  onClose,
  onSave
}) => {
  const [mode, setMode] = useState<ModalMode>(learner ? 'manual' : 'choice');
  const [formData, setFormData] = useState({
    fullname: learner?.fullname || '',
    email: learner?.email || '',
    jobTitle: learner?.jobTitle || '',
    department: learner?.department || '',
    structureId: learner?.structureId || '',
    status: learner?.status || 'active' as LearnerStatus
  });
  const [formations, setFormations] = useState<Array<{
    name: string;
    dateInscription: string;
    dateCompletion: string;
  }>>([]);
  const [saving, setSaving] = useState(false);

  // Synchroniser formData et formations quand learner change
  useEffect(() => {
    setFormData({
      fullname: learner?.fullname || '',
      email: learner?.email || '',
      jobTitle: learner?.jobTitle || '',
      department: learner?.department || '',
      structureId: learner?.structureId || '',
      status: learner?.status || 'active' as LearnerStatus
    });
    // Initialiser les formations à partir des enrollments existants
    if (learner?.enrollments && learner.enrollments.length > 0) {
      setFormations(learner.enrollments.map(e => ({
        name: e.program?.name || '',
        dateInscription: e.enrolledAt ? new Date(e.enrolledAt).toISOString().split('T')[0] : '',
        dateCompletion: e.completedAt ? new Date(e.completedAt).toISOString().split('T')[0] : ''
      })));
    } else {
      setFormations([]);
    }
    setMode(learner ? 'manual' : 'choice');
  }, [learner]);

  // Import states
  const [importedData, setImportedData] = useState<ImportedLearner[]>([]);
  const [importStructureId, setImportStructureId] = useState<string>('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Sanitize form data to prevent XSS attacks
      const sanitizedData = {
        fullname: sanitizeText(formData.fullname),
        email: sanitizeEmail(formData.email),
        jobTitle: sanitizeText(formData.jobTitle),
        department: sanitizeText(formData.department),
        structureId: formData.structureId || undefined,
        status: formData.status
      };

      // Sauvegarder l'apprenant
      await onSave(sanitizedData);

      // Parser les dates
      const parseDate = (dateStr: string): Date | null => {
        if (!dateStr?.trim()) return null;
        const str = dateStr.trim();
        if (str.includes('/')) {
          const parts = str.split('/');
          if (parts.length === 3) {
            return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          }
        }
        // Format YYYY-MM-DD from input type="date"
        const parsed = new Date(str);
        return isNaN(parsed.getTime()) ? null : parsed;
      };

      // Identifier les nouvelles formations (celles qui n'existent pas encore)
      const newFormations = formations.filter(f =>
        f.name.trim() && !learner?.enrollments?.some(e => e.program?.name === f.name)
      );

      if (newFormations.length > 0) {
        let targetLearnerId = learner?.id;

        // Si c'est un nouvel apprenant, le créer d'abord
        if (!learner) {
          const createdLearners = await learnersService.bulkCreate([{
            organizationId,
            email: sanitizedData.email,
            fullname: sanitizedData.fullname,
            department: sanitizedData.department,
            structureId: sanitizedData.structureId,
            status: sanitizedData.status
          }]);

          if (createdLearners.length > 0) {
            targetLearnerId = createdLearners[0].id;
          }
        }

        if (targetLearnerId) {
          // Créer les inscriptions pour les nouvelles formations
          for (const formation of newFormations) {
            try {
              // Sanitize formation name to prevent XSS
              const sanitizedFormationName = sanitizeText(formation.name);
              if (!sanitizedFormationName) continue;

              const program = await trainingProgramsService.getOrCreateByName(
                organizationId,
                sanitizedFormationName
              );

              const enrolledAt = parseDate(formation.dateInscription) || new Date();
              const completedAt = parseDate(formation.dateCompletion);

              if (completedAt) {
                await enrollmentsService.createCompleted(
                  targetLearnerId,
                  program.id,
                  enrolledAt,
                  completedAt
                );
              } else {
                await enrollmentsService.create({
                  learnerId: targetLearnerId,
                  programId: program.id,
                  enrolledAt: enrolledAt
                });
              }
            } catch (err) {
              console.warn('Error creating enrollment:', err);
            }
          }
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const addFormation = () => {
    setFormations([...formations, { name: '', dateInscription: '', dateCompletion: '' }]);
  };

  const removeFormation = (index: number) => {
    setFormations(formations.filter((_, i) => i !== index));
  };

  const updateFormation = (index: number, field: 'name' | 'dateInscription' | 'dateCompletion', value: string) => {
    const updated = [...formations];
    updated[index][field] = value;
    setFormations(updated);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImportError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet);

        // Helper pour trouver une valeur par clé (insensible à la casse et aux espaces)
        const findValue = (row: Record<string, unknown>, keys: string[]): string => {
          // Chercher d'abord avec les clés exactes
          for (const key of keys) {
            if (row[key] !== undefined && row[key] !== null) {
              return String(row[key]);
            }
          }
          // Chercher avec normalisation (minuscules, sans espaces multiples)
          const normalizedRow = Object.entries(row).reduce((acc, [k, v]) => {
            acc[k.toLowerCase().trim().replace(/\s+/g, ' ')] = v;
            return acc;
          }, {} as Record<string, unknown>);

          for (const key of keys) {
            const normalizedKey = key.toLowerCase().trim().replace(/\s+/g, ' ');
            if (normalizedRow[normalizedKey] !== undefined && normalizedRow[normalizedKey] !== null) {
              return String(normalizedRow[normalizedKey]);
            }
          }
          return '';
        };

        // Helper pour convertir les dates Excel (nombre ou texte)
        const parseExcelDate = (value: unknown): string => {
          if (!value) return '';

          const strValue = String(value).trim();

          // Si c'est "En cours" ou similaire, le garder tel quel
          if (strValue.toLowerCase() === 'en cours' || strValue === '-' || strValue === '') {
            return '';
          }

          // Si c'est un nombre (format Excel pour les dates) - soit number soit string numérique
          const numValue = typeof value === 'number' ? value : parseFloat(strValue);
          if (!isNaN(numValue) && numValue > 40000 && numValue < 60000) {
            // Excel stocke les dates comme nombre de jours depuis le 1er janvier 1900
            const excelEpoch = new Date(1899, 11, 30); // Excel bug: 1900 n'est pas bissextile
            const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          }

          return strValue;
        };

        // Helper pour trouver une valeur brute (pour les dates)
        const findRawValue = (row: Record<string, unknown>, keys: string[]): unknown => {
          for (const key of keys) {
            if (row[key] !== undefined && row[key] !== null) {
              return row[key];
            }
          }
          // Chercher avec normalisation
          const normalizedRow = Object.entries(row).reduce((acc, [k, v]) => {
            acc[k.toLowerCase().trim().replace(/\s+/g, ' ')] = v;
            return acc;
          }, {} as Record<string, unknown>);

          for (const key of keys) {
            const normalizedKey = key.toLowerCase().trim().replace(/\s+/g, ' ');
            if (normalizedRow[normalizedKey] !== undefined && normalizedRow[normalizedKey] !== null) {
              return normalizedRow[normalizedKey];
            }
          }
          return null;
        };

        // Map Excel columns to learner fields and sanitize to prevent XSS
        const mapped: ImportedLearner[] = jsonData.map(row => {
          const rowRecord = row as Record<string, unknown>;

          const dateInscriptionRaw = findRawValue(rowRecord, ["Date d'inscription", "Date d inscription", 'Date inscription', 'DateInscription', 'Inscription', 'Enrolled', 'Start date']);
          const dateCompletionRaw = findRawValue(rowRecord, ['Date de complétion', 'Date de completion', 'Date complétion', 'Date completion', 'DateCompletion', 'Complétion', 'Completion', 'End date', 'Completed']);

          // Extract raw values
          const rawLearner = {
            fullname: findValue(rowRecord, ['Nom et Prénom', 'Nom et Prenom', 'Nom', 'Nom complet', 'fullname', 'name', 'Prénom Nom', 'Prenom Nom']),
            email: findValue(rowRecord, ['Email', 'email', 'Mail', 'E-mail', 'Adresse email', 'Adresse mail']),
            groupe: findValue(rowRecord, ['Groupe', 'groupe', 'Group', 'Département', 'Departement', 'Department']),
            formation: findValue(rowRecord, ['Formation', 'Formations', 'Formations achevées', 'Formation achevée', 'Programme', 'Training']),
            dateInscription: parseExcelDate(dateInscriptionRaw),
            dateCompletion: parseExcelDate(dateCompletionRaw)
          };

          // Sanitize and validate all fields
          return validateAndSanitizeLearner(rawLearner);
        }).filter(l => l.fullname && l.email);

        console.log('Mapped data:', mapped.slice(0, 3)); // Debug log

        if (mapped.length === 0) {
          setImportError('Aucun apprenant valide trouvé. Assurez-vous que le fichier contient les colonnes "Nom et Prénom" et "Email".');
          return;
        }

        // Check for validation errors (chronological issues)
        const invalidRows = mapped.filter(l => !l.isValid);
        if (invalidRows.length > 0) {
          const errorMessages = invalidRows.slice(0, 3).map(l =>
            `${l.fullname}: ${l.validationErrors?.join(', ')}`
          ).join('\n');
          const moreText = invalidRows.length > 3 ? `\n...et ${invalidRows.length - 3} autres erreurs` : '';
          setImportError(`Erreurs de validation:\n${errorMessages}${moreText}`);
          return;
        }

        setImportedData(mapped);
      } catch (err) {
        console.error('Import parsing error:', err);
        setImportError('Erreur lors de la lecture du fichier. Assurez-vous qu\'il s\'agit d\'un fichier Excel valide.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (importedData.length === 0) return;

    setImporting(true);
    setImportError(null);

    try {
      console.log('Starting import with', importedData.length, 'rows');

      // Dédupliquer les apprenants par email
      const uniqueLearners = new Map<string, ImportedLearner>();
      importedData.forEach(learner => {
        if (!uniqueLearners.has(learner.email)) {
          uniqueLearners.set(learner.email, learner);
        }
      });

      console.log('Unique learners:', uniqueLearners.size);

      // 1. Créer les apprenants
      const learnersToCreate = Array.from(uniqueLearners.values()).map(learner => ({
        organizationId,
        structureId: importStructureId || undefined,
        email: learner.email,
        fullname: learner.fullname,
        department: learner.groupe,
        status: 'active' as LearnerStatus
      }));

      console.log('Creating learners...');
      const createdLearners = await learnersService.bulkCreate(learnersToCreate);
      console.log('Created learners:', createdLearners.length);

      // Créer un map email -> learner pour retrouver les IDs
      const learnerByEmail = new Map<string, { id: string }>();
      createdLearners.forEach(l => learnerByEmail.set(l.email, l));

      // 2. Pour chaque ligne avec une formation, créer l'enrollment
      const formationsToProcess = importedData.filter(r => r.formation?.trim());
      console.log('Formations to process:', formationsToProcess.length);

      // Helper pour parser les dates (format DD/MM/YYYY ou YYYY-MM-DD)
      const parseDate = (dateStr: string | undefined): Date | null => {
        if (!dateStr?.trim()) return null;
        const str = dateStr.trim();
        if (str.includes('/')) {
          const parts = str.split('/');
          if (parts.length === 3) {
            return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          }
        }
        const parsed = new Date(str);
        return isNaN(parsed.getTime()) ? null : parsed;
      };

      for (let i = 0; i < formationsToProcess.length; i++) {
        const row = formationsToProcess[i];
        console.log(`Processing formation ${i + 1}/${formationsToProcess.length}: ${row.formation}`);

        const learner = learnerByEmail.get(row.email);
        if (learner) {
          try {
            // Trouver ou créer le programme de formation
            const program = await trainingProgramsService.getOrCreateByName(
              organizationId,
              row.formation!.trim()
            );
            console.log('Program found/created:', program.id, program.name);

            const enrolledAt = parseDate(row.dateInscription) || new Date();
            const completedAt = parseDate(row.dateCompletion);

            if (completedAt) {
              // Formation achevée - créer avec statut "completed"
              await enrollmentsService.createCompleted(
                learner.id,
                program.id,
                enrolledAt,
                completedAt
              );
              console.log('Completed enrollment created for', row.email);
            } else {
              // Formation en cours - créer avec statut "in_progress"
              await enrollmentsService.create({
                learnerId: learner.id,
                programId: program.id,
                enrolledAt: enrolledAt
              });
              console.log('In-progress enrollment created for', row.email);
            }
          } catch (e) {
            console.warn('Error creating enrollment:', e);
          }
        }
      }

      console.log('Import completed successfully');
      alert(`Import réussi ! ${createdLearners.length} apprenant(s) créé(s).`);
      window.location.reload();
    } catch (error) {
      console.error('Import error:', error);
      setImportError(`Erreur: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      { 'Nom et Prénom': 'Jean Dupont', 'Groupe': 'Commercial', 'Email': 'jean.dupont@example.com', 'Formation': 'Formation Sécurité', "Date d'inscription": '15/01/2025', 'Date de complétion': '15/02/2025' },
      { 'Nom et Prénom': 'Jean Dupont', 'Groupe': 'Commercial', 'Email': 'jean.dupont@example.com', 'Formation': 'Formation RGPD', "Date d'inscription": '20/02/2025', 'Date de complétion': '' },
      { 'Nom et Prénom': 'Marie Martin', 'Groupe': 'Technique', 'Email': 'marie.martin@example.com', 'Formation': 'Formation Qualité', "Date d'inscription": '10/03/2025', 'Date de complétion': '25/03/2025' }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Apprenants');
    XLSX.writeFile(wb, 'modele_import_apprenants.xlsx');
  };

  // Choice screen
  if (mode === 'choice') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Ajouter des apprenants
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <button
              onClick={() => setMode('manual')}
              className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 transition-all flex items-center gap-4"
            >
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Ajout manuel</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Ajouter un apprenant en remplissant le formulaire
                </p>
              </div>
            </button>

            <button
              onClick={() => setMode('import')}
              className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:border-primary hover:bg-primary/5 transition-all flex items-center gap-4"
            >
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900 dark:text-white">Import Excel</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Importer plusieurs apprenants depuis un fichier Excel
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Import screen
  if (mode === 'import') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setMode('choice'); setImportedData([]); setImportError(null); }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Import Excel
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Structure selector */}
            {structures.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Structure (optionnel)
                </label>
                <select
                  value={importStructureId}
                  onChange={(e) => setImportStructureId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
                >
                  <option value="">Aucune structure</option>
                  {structures.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* File upload area */}
            {importedData.length === 0 ? (
              <div className="space-y-4">
                {/* Format notice */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
                    Format attendu du fichier Excel
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mb-2">
                    Le fichier doit contenir les colonnes suivantes (dans cet ordre) :
                  </p>
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b border-blue-200 dark:border-blue-700">
                          <th className="text-left py-1 px-2 text-blue-800 dark:text-blue-300">Colonne</th>
                          <th className="text-left py-1 px-2 text-blue-800 dark:text-blue-300">Obligatoire</th>
                          <th className="text-left py-1 px-2 text-blue-800 dark:text-blue-300">Description</th>
                        </tr>
                      </thead>
                      <tbody className="text-blue-700 dark:text-blue-400">
                        <tr>
                          <td className="py-1 px-2 font-mono">Nom et Prénom</td>
                          <td className="py-1 px-2">Oui</td>
                          <td className="py-1 px-2">Nom complet de l'apprenant</td>
                        </tr>
                        <tr>
                          <td className="py-1 px-2 font-mono">Groupe</td>
                          <td className="py-1 px-2">Non</td>
                          <td className="py-1 px-2">Groupe ou département de l'apprenant</td>
                        </tr>
                        <tr>
                          <td className="py-1 px-2 font-mono">Email</td>
                          <td className="py-1 px-2">Oui</td>
                          <td className="py-1 px-2">Adresse email</td>
                        </tr>
                        <tr>
                          <td className="py-1 px-2 font-mono">Formation</td>
                          <td className="py-1 px-2">Non</td>
                          <td className="py-1 px-2">Nom de la formation (une ligne par formation)</td>
                        </tr>
                        <tr>
                          <td className="py-1 px-2 font-mono">Date d'inscription</td>
                          <td className="py-1 px-2">Non</td>
                          <td className="py-1 px-2">Date d'inscription à la formation</td>
                        </tr>
                        <tr>
                          <td className="py-1 px-2 font-mono">Date de complétion</td>
                          <td className="py-1 px-2">Non</td>
                          <td className="py-1 px-2">Date de fin (vide si formation en cours)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 italic">
                    Note : Si un apprenant a plusieurs formations, ajoutez une ligne par formation avec les mêmes informations (Nom, Groupe, Email).
                  </p>
                </div>

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                >
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-300 font-medium">
                    Cliquez pour sélectionner un fichier Excel
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Formats supportés : .xlsx, .xls
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </div>

                <button
                  onClick={downloadTemplate}
                  className="w-full px-4 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
                >
                  Télécharger le modèle Excel
                </button>

                {importError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{importError}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{importedData.length} apprenant(s) prêt(s) à être importé(s)</p>
                </div>

                {/* Preview table */}
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Nom et Prénom</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Groupe</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Email</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Formation</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Inscription</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Complétion</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {importedData.slice(0, 50).map((learner, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-3 py-2 text-gray-900 dark:text-white">{safeText(learner.fullname)}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{safeText(learner.groupe) || '-'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{safeText(learner.email)}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{safeText(learner.formation) || '-'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{safeText(learner.dateInscription) || '-'}</td>
                            <td className="px-3 py-2">
                              {learner.dateCompletion ? (
                                <span className="text-green-600 dark:text-green-400">{safeText(learner.dateCompletion)}</span>
                              ) : (
                                <span className="text-orange-500 dark:text-orange-400">En cours</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {importedData.length > 50 && (
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm text-gray-500 dark:text-gray-400 text-center">
                      Et {importedData.length - 50} autres...
                    </div>
                  )}
                </div>

                {importError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{importError}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => { setImportedData([]); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Choisir un autre fichier
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={importing}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {importing ? 'Import en cours...' : `Importer ${importedData.length} apprenant(s)`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Manual form
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {!learner && (
              <button
                onClick={() => setMode('choice')}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {learner ? 'Modifier l\'apprenant' : 'Nouvel apprenant'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom complet *
            </label>
            <input
              type="text"
              required
              value={formData.fullname}
              onChange={(e) => setFormData({ ...formData, fullname: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {structures.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Structure
              </label>
              <select
                value={formData.structureId}
                onChange={(e) => setFormData({ ...formData, structureId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                <option value="">Aucune structure</option>
                {structures.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Groupe
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
          </div>

          {/* Section Formations */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Formations
              </label>
              <button
                type="button"
                onClick={addFormation}
                className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Ajouter une formation
              </button>
            </div>

            {formations.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                Aucune formation
              </p>
            ) : (
              <div className="space-y-3">
                {formations.map((formation, index) => {
                  // Vérifier si c'est une formation existante (a un enrollment correspondant)
                  const isExistingFormation = learner?.enrollments?.some(e => e.program?.name === formation.name);
                  return (
                    <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          Formation {index + 1} {isExistingFormation && <span className="text-blue-500">(existante)</span>}
                        </span>
                        {!isExistingFormation && (
                          <button
                            type="button"
                            onClick={() => removeFormation(index)}
                            className="text-red-500 hover:text-red-600 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Nom de la formation"
                        value={formation.name}
                        readOnly={isExistingFormation}
                        onChange={(e) => updateFormation(index, 'name', e.target.value)}
                        className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white ${isExistingFormation ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/50 focus:border-primary'}`}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Date d'inscription
                          </label>
                          <input
                            type="date"
                            value={formation.dateInscription}
                            readOnly={isExistingFormation}
                            onChange={(e) => updateFormation(index, 'dateInscription', e.target.value)}
                            className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white ${isExistingFormation ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/50 focus:border-primary'}`}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Date de complétion
                          </label>
                          <input
                            type="date"
                            value={formation.dateCompletion}
                            readOnly={isExistingFormation}
                            onChange={(e) => updateFormation(index, 'dateCompletion', e.target.value)}
                            className={`w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white ${isExistingFormation ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700 focus:ring-2 focus:ring-primary/50 focus:border-primary'}`}
                          />
                        </div>
                      </div>
                      {!formation.dateCompletion && formation.dateInscription && (
                        <p className="text-xs text-orange-500 dark:text-orange-400">
                          {isExistingFormation ? 'En cours' : 'Sans date de complétion, la formation sera marquée "En cours"'}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Learner Details Modal
interface LearnerDetailsModalProps {
  learner: LearnerWithProgress;
  onClose: () => void;
}

const LearnerDetailsModal: React.FC<LearnerDetailsModalProps> = ({ learner, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {learner.fullname}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{learner.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Groupe</p>
              <p className="text-gray-900 dark:text-white">{learner.department || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Progression globale</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                  <div
                    className="h-2 bg-primary rounded-full"
                    style={{ width: `${learner.overallProgress}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {learner.overallProgress}%
                </span>
              </div>
            </div>
          </div>

          {/* Enrollments */}
          {learner.enrollments && learner.enrollments.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
                Formations
              </h3>
              <div className="space-y-3">
                {learner.enrollments.map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {enrollment.program?.name || 'Programme'}
                      </h4>
                      <EnrollmentStatusBadge status={enrollment.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Date d'inscription</p>
                        <p className="text-gray-900 dark:text-white">
                          {enrollment.enrolledAt ? new Date(enrollment.enrolledAt).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Date de complétion</p>
                        <p className={enrollment.completedAt ? "text-green-600 dark:text-green-400" : "text-orange-500 dark:text-orange-400"}>
                          {enrollment.completedAt ? new Date(enrollment.completedAt).toLocaleDateString('fr-FR') : 'En cours'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                        <div
                          className="h-2 bg-primary rounded-full"
                          style={{ width: `${enrollment.progressPercent}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {enrollment.progressPercent}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// Enrollment Status Badge
const EnrollmentStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    not_started: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
  };

  const labels = {
    not_started: 'Non commencé',
    in_progress: 'En cours',
    completed: 'Terminé',
    overdue: 'En retard'
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles] || styles.not_started}`}>
      {labels[status as keyof typeof labels] || status}
    </span>
  );
};
